import React, { useState, useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import Tooltip from "@mui/material/Tooltip";

import { useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import BlingBillingRecipient from "../BlingBillingRecipient";

const EMPTY_ITEM = { descricao: "", qtd: 1, valorUnit: 0, valorTotal: 0, unidade: "UN", pricingTableId: null, tierLabel: null };
const UNIDADES = ["UN", "PC", "CX", "KG", "G", "L", "ML", "M", "M²", "M³", "H", "SV"];

async function fetchPricingTable(blingProductId) {
  try {
    const { data } = await api.get("/catalog/pricing-tables", { params: { blingProductId: String(blingProductId) } });
    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  } catch { return null; }
}

// Componente de busca e cadastro de cliente no Bling
const BlingClientSearch = ({ clientName, clientPhone, contactId, onChange }) => {
  const [searchText, setSearchText] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const timer = useRef(null);

  const hasSelection = !!(clientName);

  const doSearch = (text) => {
    clearTimeout(timer.current);
    setNoResults(false);
    if (!text || text.trim().length < 2) { setOptions([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const isPhone = /^[\d\s()+.-]{8,}$/.test(text.trim());
        const params = isPhone ? { telefone: text.trim() } : { nome: text.trim() };
        const { data } = await api.get("/bling/contacts/search", { params });
        const list = Array.isArray(data) ? data : [];
        setOptions(list);
        setNoResults(list.length === 0);
        setOpen(true);
      } catch { setOptions([]); }
      finally { setLoading(false); }
    }, 350);
  };

  const handleSelect = async (opt) => {
    setOpen(false); setSearchText(""); setOptions([]); setNoResults(false);
    try {
      const { data } = await api.post("/bling/contacts/resolve", {
        nome: opt.nome,
        phone: opt.celular || opt.telefone || "",
        blingContactId: opt.id,
        contactId: contactId || undefined,
      });
      onChange({
        clientName: opt.nome,
        clientPhone: opt.celular || opt.telefone || clientPhone || "",
        contactId: data.contactId || contactId || null,
      });
    } catch {
      onChange({ clientName: opt.nome, clientPhone: opt.celular || opt.telefone || clientPhone || "" });
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) { toast.warn("Informe o nome do cliente."); return; }
    setSaving(true);
    try {
      // Se tiver telefone, tenta encontrar no Bling primeiro para evitar duplicata
      if (newPhone.trim()) {
        const { data: found } = await api.get("/bling/contacts/search", { params: { telefone: newPhone.trim() } }).catch(() => ({ data: [] }));
        if (Array.isArray(found) && found.length > 0) {
          const existing = found[0];
          toast.info(`Cliente encontrado no Bling: ${existing.nome}. Vinculando...`);
          const { data } = await api.post("/bling/contacts/resolve", {
            nome: existing.nome,
            phone: newPhone.trim(),
            blingContactId: existing.id,
            contactId: contactId || undefined,
          });
          onChange({ clientName: existing.nome, clientPhone: newPhone.trim(), contactId: data.contactId || contactId || null });
          setCreating(false); setNewName(""); setNewPhone("");
          return;
        }
      }
      const { data } = await api.post("/bling/contacts/resolve", {
        nome: newName.trim(),
        phone: newPhone.trim(),
        contactId: contactId || undefined,
      });
      toast.success("Cliente cadastrado no Bling");
      onChange({
        clientName: newName.trim(),
        clientPhone: newPhone.trim(),
        contactId: data.contactId || contactId || null,
      });
      setCreating(false); setNewName(""); setNewPhone("");
    } catch (err) {
      toastError(err);
    } finally { setSaving(false); }
  };

  const handleClear = () => {
    onChange({ clientName: "", clientPhone: "", contactId: null });
    setSearchText(""); setCreating(false);
  };

  if (hasSelection && !creating) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Chip
          label={`${clientName}${clientPhone ? ` · ${clientPhone}` : ""}`}
          onDelete={handleClear}
          style={{ backgroundColor: "#fff3e0", color: "#C05000", fontWeight: 600, maxWidth: 340 }}
        />
        <Button size="small" onClick={handleClear} style={{ color: "#888", fontSize: 11 }}>
          Alterar
        </Button>
      </div>
    );
  }

  if (creating) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            label="Nome do cliente *" size="small" style={{ flex: 2, minWidth: 180 }}
            value={newName} onChange={e => setNewName(e.target.value)}
            autoFocus
          />
          <TextField
            label="Telefone / WhatsApp" size="small" style={{ flex: 1, minWidth: 140 }}
            value={newPhone} onChange={e => setNewPhone(e.target.value)}
          />
          <Button
            variant="contained" size="small" onClick={handleCreate} disabled={saving}
            style={{ backgroundColor: "#F37021", color: "#fff", whiteSpace: "nowrap" }}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <PersonAddIcon />}
          >
            Cadastrar
          </Button>
          <Button size="small" onClick={() => { setCreating(false); setNewName(""); setNewPhone(""); }}>
            Cancelar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <TextField
        label="Buscar cliente no Bling (nome ou telefone)" size="small" fullWidth
        value={searchText}
        onChange={e => { setSearchText(e.target.value); doSearch(e.target.value); }}
        onBlur={() => setTimeout(() => { setOpen(false); setNoResults(false); }, 200)}
        placeholder="Digite nome ou número..."
        InputProps={{
          endAdornment: loading
            ? <CircularProgress size={14} style={{ color: "#F37021" }} />
            : <SearchIcon fontSize="small" style={{ color: "#bbb" }} />,
        }}
      />
      {open && options.length > 0 && (
        <div style={{
          position: "absolute", zIndex: 1400, top: "100%", left: 0, right: 0,
          background: "#fff", border: "1px solid #ddd", borderRadius: 6,
          boxShadow: "0 4px 16px rgba(0,0,0,.12)", maxHeight: 280, overflowY: "auto",
        }}>
          {options.map(opt => (
            <div key={opt.id} onMouseDown={() => handleSelect(opt)}
              style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f5f5f5" }}
              onMouseEnter={e => e.currentTarget.style.background = "#fff8f4"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}
            >
              <div style={{ fontWeight: 600, fontSize: 13, color: "#222" }}>{opt.nome}</div>
              {(opt.celular || opt.email) && (
                <div style={{ fontSize: 11, color: "#888", marginTop: 2, display: "flex", gap: 8 }}>
                  {opt.celular && <span>{opt.celular}</span>}
                  {opt.email && <span>{opt.email}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {noResults && !loading && searchText.trim().length >= 2 && (
        <div style={{
          position: "absolute", zIndex: 1400, top: "100%", left: 0, right: 0,
          background: "#fff", border: "1px solid #ddd", borderRadius: 6,
          boxShadow: "0 4px 16px rgba(0,0,0,.12)", padding: "10px 14px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        }}>
          <span style={{ fontSize: 12, color: "#999" }}>Nenhum cliente encontrado.</span>
          <Button
            size="small" variant="outlined" onClick={() => { setOpen(false); setCreating(true); setNewName(searchText); }}
            startIcon={<PersonAddIcon />}
            style={{ borderColor: "#F37021", color: "#F37021", fontSize: 11 }}
          >
            Cadastrar novo
          </Button>
        </div>
      )}
      {!open && !noResults && (
        <div style={{ marginTop: 4, textAlign: "right" }}>
          <Button size="small" onClick={() => setCreating(true)}
            startIcon={<PersonAddIcon />}
            style={{ color: "#F37021", fontSize: 11, padding: "0 4px" }}>
            Cadastrar novo cliente
          </Button>
        </div>
      )}
    </div>
  );
};

function highlightTerms(text, terms) {
  if (!text || !terms?.length) return text;
  const normalize = s => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const norm = normalize(text);
  const parts = [];
  let last = 0;
  const matches = [];
  for (const term of terms) {
    const t = normalize(term);
    if (!t) continue;
    let idx = 0;
    while ((idx = norm.indexOf(t, idx)) !== -1) {
      matches.push({ start: idx, end: idx + t.length });
      idx += t.length;
    }
  }
  if (!matches.length) return text;
  matches.sort((a, b) => a.start - b.start);
  for (const m of matches) {
    if (m.start < last) continue;
    if (m.start > last) parts.push(text.slice(last, m.start));
    parts.push(<mark key={m.start} style={{ background: "#FFE0C2", color: "#C05000", padding: 0, borderRadius: 2 }}>{text.slice(m.start, m.end)}</mark>);
    last = m.end;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

const BlingItemSearch = ({ value, onTextChange, onProductSelect }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [lastQuery, setLastQuery] = useState("");
  const [noResults, setNoResults] = useState(false);
  const timer = useRef(null);

  const search = (text) => {
    clearTimeout(timer.current);
    if (!text || text.trim().length < 1) { setOptions([]); setOpen(false); setNoResults(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true); setNoResults(false);
      try {
        const { data } = await api.get("/bling/products", { params: { search: text.trim(), limit: 15 } });
        const list = Array.isArray(data) ? data : [];
        setOptions(list); setLastQuery(text.trim()); setOpen(true); setNoResults(list.length === 0);
      } catch { setOptions([]); setOpen(false); setNoResults(false); }
      finally { setLoading(false); }
    }, 400);
  };

  const handleChange = (e) => { onTextChange(e.target.value); search(e.target.value); };
  const handleSelect = (opt) => {
    onTextChange(opt.nome);
    onProductSelect({ valorUnit: opt.preco || 0, id: opt.id, ncm: opt.ncm, codigo: opt.codigo, unidade: opt.unidade });
    setOptions([]); setOpen(false); setNoResults(false);
  };
  const searchWords = lastQuery.trim().split(/\s+/).filter(Boolean);

  return (
    <div style={{ position: "relative" }}>
      <TextField
        label="Descrição" size="small" fullWidth value={value} onChange={handleChange}
        onBlur={() => setTimeout(() => { setOpen(false); setNoResults(false); }, 180)}
        placeholder="Nome, palavras-chave ou código Bling..."
        InputProps={{
          endAdornment: loading
            ? <CircularProgress size={14} style={{ color: "#F37021" }} />
            : <SearchIcon fontSize="small" style={{ color: "#bbb" }} />,
        }}
      />
      {open && options.length > 0 && (
        <div style={{
          position:"absolute", zIndex:1400, top:"100%", left:0, right:0,
          background:"#fff", border:"1px solid #ddd", borderRadius:6,
          boxShadow:"0 4px 16px rgba(0,0,0,.12)", maxHeight:300, overflowY:"auto",
        }}>
          {options.map(opt => {
            const isVar = opt._isVariacao;
            const displayNome = isVar && opt._parentNome
              ? opt.nome.replace(opt._parentNome, "").trim().replace(/^[-–—\s]+/, "")
              : opt.nome;
            return (
              <div key={opt.id} onMouseDown={() => handleSelect(opt)}
                style={{
                  padding: isVar ? "5px 12px 5px 18px" : "7px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f5f5f5",
                  borderLeft: isVar ? "3px solid #F37021" : "3px solid transparent",
                }}
                onMouseEnter={e => e.currentTarget.style.background="#fff8f4"}
                onMouseLeave={e => e.currentTarget.style.background="#fff"}
              >
                {isVar && opt._parentNome && (
                  <div style={{ fontSize:10, color:"#bbb", marginBottom:1, lineHeight:1.2 }}>
                    {opt._parentNome}
                  </div>
                )}
                <div style={{ fontWeight:600, fontSize:13, color:"#222", lineHeight:1.3 }}>
                  {highlightTerms(displayNome || opt.nome, searchWords)}
                </div>
                <div style={{ fontSize:11, color:"#888", marginTop:2, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  {opt.codigo && (
                    <span style={{
                      fontFamily:"monospace",
                      background: isVar ? "#e8f4e8" : "#f0f0f0",
                      color: isVar ? "#1a5c1a" : "#555",
                      fontWeight: isVar ? 700 : 400,
                      borderRadius:3, padding:"0 4px",
                    }}>
                      {highlightTerms(opt.codigo, searchWords)}
                    </span>
                  )}
                  {opt.preco > 0 && (
                    <span style={{ color:"#2a6030", fontWeight:600 }}>
                      {Number(opt.preco).toLocaleString("pt-BR", { style:"currency", currency:"BRL" })}
                    </span>
                  )}
                  {opt.estoque != null && typeof opt.estoque !== "object" && (
                    <span style={{ color: opt.estoque > 0 ? "#aaa" : "#e53935" }}>
                      est: {opt.estoque} un.
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {noResults && !loading && value.trim() && (
        <div style={{
          position:"absolute", zIndex:1400, top:"100%", left:0, right:0,
          background:"#fff", border:"1px solid #ddd", borderRadius:6,
          boxShadow:"0 4px 16px rgba(0,0,0,.12)", padding:"10px 14px",
        }}>
          <span style={{ fontSize:12, color:"#999" }}>
            Nenhum produto encontrado. Tente palavras separadas (ex: "panfleto 4x4") ou o código exato.
          </span>
        </div>
      )}
    </div>
  );
};

const QuoteDialog = ({ open, onClose, onSaved, quote, initialTicketId, initialContact }) => {
  const isEdit = Boolean(quote?.id);
  const { user: currentUser } = useContext(AuthContext);

  const blankForm = () => ({
    clientName: initialContact?.name || "",
    clientPhone: initialContact?.number || "",
    contactId: initialContact?.id || null,
    ticketId: initialTicketId || null,
    validUntil: "",
    total: "",
    discount: "",
    paymentMethod: "",
    observations: "",
    responsibleId: currentUser?.id || "",
    items: [{ ...EMPTY_ITEM }],
    freteAtivo: false,
    freteEndereco: "",
    freteCep: "",
    freteValor: "",
    // Paridade com a OS (preservados na conversão)
    prioridade: "normal",
    deadline: "",
    installments: [],
    billingEnabled: false,
    billingBlingContactId: null,
    billingName: "",
    billingDocument: "",
  });

  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [ticketOptions, setTicketOptions] = useState([]);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketInputValue, setTicketInputValue] = useState("");
  const [blingAddress, setBlingAddress] = useState(null);
  const ticketTimer = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/users", { params: { limit: 100 } }).then(({ data }) => {
      setUsers(Array.isArray(data.users) ? data.users : []);
    }).catch(() => {});
    api.get("/bling/payment-methods").then(({ data }) => {
      setPaymentMethods(Array.isArray(data) ? data : []);
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (quote) {
      setForm({
        clientName: quote.clientName || "",
        clientPhone: quote.clientPhone || "",
        contactId: quote.contactId || null,
        ticketId: quote.ticketId || null,
        validUntil: quote.validUntil || "",
        total: quote.total != null ? String(quote.total) : "",
        paymentMethod: quote.paymentMethod || "",
        observations: quote.observations || "",
        responsibleId: quote.responsibleId || "",
        items: quote.items?.length ? quote.items : [{ ...EMPTY_ITEM }],
        freteAtivo: quote.freteAtivo || false,
        freteEndereco: quote.freteEndereco || "",
        freteCep: quote.freteCep || "",
        freteValor: quote.freteValor != null ? String(quote.freteValor) : "",
        discount: (() => { try { const p = JSON.parse(quote.paymentMethod); return p.desconto ? String(p.desconto) : ""; } catch { return ""; } })(),
        prioridade: quote.prioridade || "normal",
        deadline: quote.deadline || "",
        installments: Array.isArray(quote.installments) ? quote.installments : [],
        billingEnabled: !!quote.billingBlingContactId,
        billingBlingContactId: quote.billingBlingContactId || null,
        billingName: quote.billingName || "",
        billingDocument: quote.billingDocument || "",
      });
      if (quote.ticketId) setTicketInputValue(`#${quote.ticketId}`);
    } else {
      setForm(blankForm());
      if (initialTicketId) setTicketInputValue(`#${initialTicketId}`);
    }
  }, [open, quote]);

  // Carrega endereço Bling do cliente para sugestão de frete
  useEffect(() => {
    setBlingAddress(null);
    if (!form.contactId) return;
    api.get(`/contacts/${form.contactId}`)
      .then(async ({ data }) => {
        if (!data.blingContactId) return;
        try {
          const { data: bc } = await api.get(`/bling/contact-detail/${data.blingContactId}`);
          const end = bc?.endereco;
          if (!end?.logradouro) return;
          const parts = [end.logradouro, end.numero, end.complemento, end.bairro, end.municipio, end.uf].filter(Boolean);
          setBlingAddress({ formatted: parts.join(", "), cep: end.cep || "" });
        } catch { /* sugestão é opcional */ }
      })
      .catch(() => {});
  }, [form.contactId]);

  const handleTicketSearch = (val) => {
    setTicketInputValue(val);
    if (!val || val.length < 2) { setTicketOptions([]); return; }
    clearTimeout(ticketTimer.current);
    ticketTimer.current = setTimeout(async () => {
      setTicketLoading(true);
      try {
        const { data } = await api.get("/tickets", { params: { searchParam: val, pageNumber: 1, status: "open" } });
        setTicketOptions((data.tickets || []).slice(0, 10));
      } catch { setTicketOptions([]); }
      finally { setTicketLoading(false); }
    }, 350);
  };

  const handleTicketSelect = (ticket) => {
    if (!ticket) { setForm(f => ({ ...f, ticketId: null })); return; }
    setForm(f => ({
      ...f, ticketId: ticket.id,
      clientName: ticket.contact?.name || f.clientName,
      clientPhone: ticket.contact?.number || f.clientPhone,
      contactId: ticket.contact?.id || f.contactId,
    }));
    setTicketInputValue(`#${ticket.id} — ${ticket.contact?.name || ""}`);
    setTicketOptions([]);
  };

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const updateItemDescricao = (idx, text) => setForm(f => {
    const items = [...f.items];
    items[idx] = { ...items[idx], descricao: text };
    return { ...f, items };
  });

  const recalcTotal = (items, freteAtivo, freteValor) => {
    const itemsTotal = items.reduce((s, it) => s + (parseFloat(it.valorTotal) || 0), 0);
    const fv = freteAtivo ? (parseFloat(freteValor) || 0) : 0;
    return (itemsTotal + fv).toFixed(2);
  };

  const applyBlingProduct = async (idx, { valorUnit, id, ncm, codigo, unidade }) => {
    let pricingTable = null;
    if (id) pricingTable = await fetchPricingTable(String(id));

    setForm(f => {
      const items = [...f.items];
      const vu = pricingTable ? (valorUnit || 0) : (valorUnit || 0);
      items[idx] = {
        ...items[idx],
        valorUnit: vu,
        valorTotal: +((parseFloat(items[idx].qtd) || 1) * vu).toFixed(2),
        ncm: ncm || undefined,
        blingProductId: id || undefined,
        codigo: codigo || undefined,
        unidade: pricingTable ? pricingTable.unidade : (unidade || items[idx].unidade || "UN"),
        pricingTableId: pricingTable ? pricingTable.id : null,
        tierLabel: null,
      };
      const total = recalcTotal(items, f.freteAtivo, f.freteValor);
      return { ...f, items, total };
    });

    if (pricingTable) {
      const qtdAtual = parseFloat(form.items[idx]?.qtd) || 1;
      try {
        const { data } = await api.get(`/catalog/pricing-tables/${pricingTable.id}/calculate`, { params: { qty: qtdAtual } });
        setForm(f => {
          const items = [...f.items];
          if (!items[idx]) return f;
          items[idx] = { ...items[idx], valorUnit: data.unitPrice, valorTotal: +(qtdAtual * data.unitPrice).toFixed(2), tierLabel: data.tierLabel };
          return { ...f, items, total: recalcTotal(items, f.freteAtivo, f.freteValor) };
        });
      } catch { /* mantém preço base */ }
    }

    if (id && !ncm && !pricingTable) {
      api.get(`/bling/products/${id}`).then(({ data }) => {
        if (data.ncm) setForm(f => {
          const items = [...f.items];
          if (items[idx]) items[idx] = { ...items[idx], ncm: data.ncm };
          return { ...f, items };
        });
      }).catch(() => {});
    }
  };

  const updateItemField = (idx, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === "qtd" || field === "valorUnit") {
        const qtd = field === "qtd" ? parseFloat(value) || 0 : parseFloat(items[idx].qtd) || 0;
        const vu  = field === "valorUnit" ? parseFloat(value) || 0 : parseFloat(items[idx].valorUnit) || 0;
        items[idx].valorTotal = +(qtd * vu).toFixed(2);
      }
      const total = recalcTotal(items, f.freteAtivo, f.freteValor);
      return { ...f, items, total };
    });

    if (field === "qtd") {
      const item = form.items[idx];
      if (item?.pricingTableId) {
        const qty = parseFloat(value) || 0;
        if (qty > 0) {
          api.get(`/catalog/pricing-tables/${item.pricingTableId}/calculate`, { params: { qty } })
            .then(({ data }) => {
              setForm(f => {
                const items = [...f.items];
                if (!items[idx] || items[idx].pricingTableId !== item.pricingTableId) return f;
                items[idx] = { ...items[idx], valorUnit: data.unitPrice, valorTotal: +(qty * data.unitPrice).toFixed(2), tierLabel: data.tierLabel };
                return { ...f, items, total: recalcTotal(items, f.freteAtivo, f.freteValor) };
              });
            }).catch(() => {});
        }
      }
    }
  };

  const addItem    = () => setForm(f => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.clientName?.trim()) { toast.warn("Informe o nome do cliente."); return; }
    const items = form.items.filter(it => it.descricao?.trim());
    if (items.length === 0) { toast.warn("Adicione pelo menos um item ao orçamento."); return; }
    setSaving(true);
    try {
      // Desconto vai embutido no paymentMethod JSON (igual à OS)
      const disc = parseFloat(form.discount) || 0;
      let resolvedPaymentMethod = form.paymentMethod;
      if (form.paymentMethod) {
        try { const pm = JSON.parse(form.paymentMethod); resolvedPaymentMethod = JSON.stringify({ ...pm, desconto: disc > 0 ? disc : undefined }); } catch { /* free-text */ }
      }
      // discount e billingEnabled são só do form (não são colunas do Quote)
      const { discount: _disc, billingEnabled: _be, ...rest } = form;
      const payload = {
        ...rest,
        paymentMethod: resolvedPaymentMethod,
        items,
        freteAtivo: form.freteAtivo,
        freteEndereco: form.freteAtivo ? (form.freteEndereco || null) : null,
        freteCep: form.freteAtivo ? (form.freteCep || null) : null,
        freteValor: form.freteAtivo && form.freteValor ? parseFloat(form.freteValor) : null,
        total: form.total ? parseFloat(form.total) : items.reduce((s, it) => s + (parseFloat(it.valorTotal) || 0), 0),
        // Paridade com a OS — faturamento-terceiro só quando habilitado; parcelas se houver
        billingBlingContactId: form.billingEnabled ? (form.billingBlingContactId || null) : null,
        billingName: form.billingEnabled ? (form.billingName || null) : null,
        billingDocument: form.billingEnabled ? (form.billingDocument || null) : null,
        installments: form.installments && form.installments.length > 0 ? form.installments : null,
      };
      if (isEdit) {
        const { data } = await api.put(`/quotes/${quote.id}`, payload);
        toast.success("Orçamento atualizado");
        onSaved?.(data);
      } else {
        const { data } = await api.post("/quotes", payload);
        toast.success("Orçamento criado com sucesso");
        onSaved?.(data);
      }
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const fmtMoney = (v) => v != null ? Number(v).toLocaleString("pt-BR", { style:"currency", currency:"BRL" }) : "";
  const itemsSubtotal = form.items.reduce((s, it) => s + (parseFloat(it.valorTotal) || 0), 0);
  const freteNum = form.freteAtivo ? (parseFloat(form.freteValor) || 0) : 0;
  const computedTotal = itemsSubtotal + freteNum;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle style={{ display:"flex", alignItems:"center", gap:8, paddingBottom:8 }}>
        <RequestQuoteIcon style={{ color:"#F37021" }} />
        {isEdit ? `Editar Orçamento ${quote.numero}` : (quote?.numero ? `Duplicar ${quote.numero}` : "Novo Orçamento")}
      </DialogTitle>
      <DialogContent dividers style={{ paddingTop:16 }}>
        <Grid container spacing={2}>

          {/* ── Ticket vinculado ── */}
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={ticketOptions}
              inputValue={ticketInputValue}
              onInputChange={(_, val) => handleTicketSearch(val)}
              onChange={(_, ticket) => handleTicketSelect(ticket)}
              getOptionLabel={t => typeof t === "string" ? t : `#${t.id} — ${t.contact?.name || ""}`}
              loading={ticketLoading}
              renderInput={params => (
                <TextField {...params} label="Ticket vinculado (opcional)" size="small"
                  placeholder="Digite # ou nome do contato..."
                  InputProps={{ ...params.InputProps, endAdornment: ticketLoading ? <CircularProgress size={14} /> : params.InputProps.endAdornment }}
                />
              )}
            />
          </Grid>

          {/* ── Cliente ── */}
          <Grid item xs={12}>
            <Typography variant="caption" style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Cliente
            </Typography>
            <div style={{ marginTop: 6 }}>
              <BlingClientSearch
                clientName={form.clientName}
                clientPhone={form.clientPhone}
                contactId={form.contactId}
                onChange={({ clientName, clientPhone, contactId }) =>
                  setForm(f => ({ ...f, clientName: clientName ?? f.clientName, clientPhone: clientPhone ?? f.clientPhone, contactId: contactId !== undefined ? contactId : f.contactId }))
                }
              />
            </div>
          </Grid>

          {/* ── Validade + Forma de pagamento ── */}
          <Grid item xs={12} sm={6}>
            <TextField label="Válido até" type="date" size="small" fullWidth
              value={form.validUntil} onChange={set("validUntil")}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          {/* Paridade com a OS: prazo, desconto, prioridade */}
          <Grid item xs={12} sm={6}>
            <TextField label="Prazo de produção" type="date" size="small" fullWidth
              value={form.deadline} onChange={set("deadline")}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Desconto (R$)" type="number" size="small" fullWidth
              value={form.discount} onChange={set("discount")}
              inputProps={{ min: 0, step: "0.01" }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl size="small" fullWidth>
              <InputLabel id="prio-label">Prioridade</InputLabel>
              <Select labelId="prio-label" label="Prioridade" value={form.prioridade} onChange={set("prioridade")}>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="urgente">Urgente</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            {paymentMethods.length > 0 ? (
              <Autocomplete
                options={paymentMethods}
                getOptionLabel={(opt) => typeof opt === "string" ? opt : opt.descricao}
                value={(() => {
                  try { const p = JSON.parse(form.paymentMethod); return paymentMethods.find(m => m.id === p.id) || null; }
                  catch { return null; }
                })()}
                onChange={(_, opt) => setForm(f => ({ ...f, paymentMethod: opt ? JSON.stringify({ id: opt.id, descricao: opt.descricao }) : "" }))}
                renderInput={params => <TextField {...params} label="Forma de pagamento" size="small" />}
              />
            ) : (
              <TextField label="Forma de pagamento" size="small" fullWidth
                value={form.paymentMethod} onChange={set("paymentMethod")} />
            )}
          </Grid>

          {/* === FATURAMENTO EM NOME DE TERCEIRO (paridade com a OS) === */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.billingEnabled}
                  onChange={e => setForm(f => ({
                    ...f,
                    billingEnabled: e.target.checked,
                    ...(e.target.checked ? {} : { billingBlingContactId: null, billingName: "", billingDocument: "" }),
                  }))}
                  size="small"
                />
              }
              label={
                <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 600 }}>
                  🧾 Faturar em nome de outra pessoa/empresa
                </span>
              }
            />
            {form.billingEnabled && (
              <div style={{ marginTop: 4 }}>
                <BlingBillingRecipient
                  value={{
                    billingBlingContactId: form.billingBlingContactId,
                    billingName: form.billingName,
                    billingDocument: form.billingDocument,
                  }}
                  onChange={(v) => setForm(f => ({ ...f, ...v }))}
                />
                <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#888" }}>
                  Ao converter em OS, a NF e o pedido no Bling sairão no nome deste destinatário.
                </span>
              </div>
            )}
          </Grid>

          {/* === PLANO DE PARCELAS (proposta — sem controle de recebimento) === */}
          <Grid item xs={12}>
            <Divider style={{ marginBottom: 8 }} />
            {(() => {
              const netTotal = Math.max(0, computedTotal - (parseFloat(form.discount) || 0));
              const applyPreset = (kind) => {
                const t = +netTotal.toFixed(2);
                const today = new Date().toISOString().slice(0, 10);
                const plus = (m) => { const d = new Date(); d.setMonth(d.getMonth() + m); return d.toISOString().slice(0, 10); };
                if (kind === "5050") {
                  const half = +(t / 2).toFixed(2);
                  setForm(f => ({ ...f, installments: [
                    { label: "1ª parcela", amount: half, dueDate: today },
                    { label: "2ª parcela", amount: +(t - half).toFixed(2), dueDate: plus(1) },
                  ] }));
                } else if (kind === "3070") {
                  const entrada = +(t * 0.3).toFixed(2);
                  setForm(f => ({ ...f, installments: [
                    { label: "Entrada (30%)", amount: entrada, dueDate: today },
                    { label: "Saldo (70%)", amount: +(t - entrada).toFixed(2), dueDate: plus(1) },
                  ] }));
                } else if (kind === "3x") {
                  const third = +(t / 3).toFixed(2);
                  setForm(f => ({ ...f, installments: [
                    { label: "1ª parcela", amount: third, dueDate: today },
                    { label: "2ª parcela", amount: third, dueDate: plus(1) },
                    { label: "3ª parcela", amount: +(t - 2 * third).toFixed(2), dueDate: plus(2) },
                  ] }));
                }
              };
              const addInstallment = () => setForm(f => ({ ...f, installments: [...(f.installments || []), { label: `Parcela ${(f.installments?.length || 0) + 1}`, amount: "", dueDate: "" }] }));
              const updateInstallment = (idx, field, value) => setForm(f => {
                const installments = [...f.installments];
                installments[idx] = { ...installments[idx], [field]: value };
                return { ...f, installments };
              });
              const removeInstallment = (idx) => setForm(f => ({ ...f, installments: f.installments.filter((_, i) => i !== idx) }));
              const planTotal = (form.installments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
              return (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                    <Typography variant="subtitle2" color="textSecondary" style={{ textTransform: "uppercase", fontSize: 11, letterSpacing: .7 }}>
                      Plano de parcelas
                    </Typography>
                    {netTotal > 0 && (
                      <>
                        <Chip size="small" label="50/50" onClick={() => applyPreset("5050")} clickable style={{ fontSize: 11 }} />
                        <Chip size="small" label="30/70 entrada" onClick={() => applyPreset("3070")} clickable style={{ fontSize: 11 }} />
                        <Chip size="small" label="3× iguais" onClick={() => applyPreset("3x")} clickable style={{ fontSize: 11 }} />
                      </>
                    )}
                    <Button size="small" startIcon={<AddIcon />} onClick={addInstallment} style={{ color: "#F37021", marginLeft: "auto" }}>
                      Parcela
                    </Button>
                  </div>
                  {(form.installments || []).length > 0 && (
                    <div style={{ border: "1px solid #e0e0e0", borderRadius: 6, overflow: "hidden", marginBottom: 8 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 130px 36px", gap: 0, background: "#f5f5f5", padding: "4px 8px" }}>
                        {["Descrição", "Valor (R$)", "Vencimento", ""].map(h => (
                          <span key={h} style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#666", letterSpacing: .5 }}>{h}</span>
                        ))}
                      </div>
                      {form.installments.map((p, idx) => (
                        <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 110px 130px 36px", gap: 0, padding: "4px 8px", borderTop: "1px solid #eee", alignItems: "center" }}>
                          <TextField size="small" variant="standard" value={p.label}
                            onChange={e => updateInstallment(idx, "label", e.target.value)}
                            inputProps={{ style: { fontSize: 13 } }} />
                          <TextField size="small" variant="standard" type="number" value={p.amount}
                            onChange={e => updateInstallment(idx, "amount", e.target.value)}
                            inputProps={{ style: { fontSize: 13, textAlign: "right" }, step: "0.01", min: 0 }} />
                          <TextField size="small" variant="standard" type="date" value={p.dueDate || ""}
                            onChange={e => updateInstallment(idx, "dueDate", e.target.value)}
                            inputProps={{ style: { fontSize: 13 } }} />
                          <IconButton size="small" onClick={() => removeInstallment(idx)}>
                            <DeleteIcon fontSize="small" style={{ color: "#d32f2f" }} />
                          </IconButton>
                        </div>
                      ))}
                      <div style={{ display: "flex", gap: 16, padding: "6px 10px", borderTop: "2px solid #ddd", background: "#fafafa", justifyContent: "flex-end" }}>
                        {netTotal > 0 && (
                          <span style={{ fontSize: 12, color: "#555" }}>
                            {(parseFloat(form.discount) || 0) > 0 ? <>Líquido: <strong>{fmtMoney(netTotal)}</strong></> : <>Total: <strong>{fmtMoney(netTotal)}</strong></>}
                          </span>
                        )}
                        <span style={{ fontSize: 12, color: Math.abs(planTotal - netTotal) > 0.01 ? "#d32f2f" : "#388e3c" }}>
                          Parcelado: <strong>{fmtMoney(planTotal)}</strong>
                          {Math.abs(planTotal - netTotal) > 0.01 && netTotal > 0 ? " ⚠️ difere do total" : ""}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </Grid>

          {/* ── Responsável ── */}
          <Grid item xs={12} sm={6}>
            <TextField label="Responsável" size="small" fullWidth select
              value={form.responsibleId} onChange={set("responsibleId")}>
              <MenuItem value="">— nenhum —</MenuItem>
              {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
            </TextField>
          </Grid>

          {/* ── Itens ── */}
          <Grid item xs={12}>
            <Divider style={{ marginBottom:12 }} />
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
              <Typography variant="subtitle2">Itens do orçamento</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addItem}
                style={{ color:"#F37021" }}>
                Adicionar item
              </Button>
            </div>

            {/* Cabeçalho da tabela */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 68px 72px 110px 110px 36px", gap:6, marginBottom:4 }}>
              {["Descrição","UN","Qtd","V. Unit (R$)","Total",""].map(h => (
                <Typography key={h} variant="caption" style={{ color:"#888", fontWeight:600, textAlign: h === "Qtd" || h === "V. Unit (R$)" || h === "Total" || h === "UN" ? "center" : "left" }}>
                  {h}
                </Typography>
              ))}
            </div>

            {form.items.map((it, idx) => (
              <div key={idx} style={{ display:"grid", gridTemplateColumns:"1fr 68px 72px 110px 110px 36px", gap:6, marginBottom:6, alignItems:"flex-start" }}>
                <BlingItemSearch
                  value={it.descricao}
                  onTextChange={text => updateItemDescricao(idx, text)}
                  onProductSelect={p => applyBlingProduct(idx, p)}
                />
                <FormControl size="small" fullWidth>
                  <Select
                    value={it.unidade || "UN"}
                    onChange={e => updateItemField(idx, "unidade", e.target.value)}
                    style={{ fontSize: 12 }}
                  >
                    {UNIDADES.map(u => <MenuItem key={u} value={u} style={{ fontSize:12 }}>{u}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField size="small" type="number" inputProps={{ min:0.01, step:"any", style:{ textAlign:"center" } }}
                  value={it.qtd}
                  onChange={e => updateItemField(idx, "qtd", e.target.value)} />
                <Tooltip
                  title={it.tierLabel ? `Faixa: ${it.tierLabel}` : ""}
                  placement="top"
                  arrow
                  disableHoverListener={!it.tierLabel}
                >
                  <span>
                    <TextField size="small" type="number" inputProps={{ min:0, step:"0.01", style:{ textAlign:"right" } }}
                      value={it.valorUnit}
                      onChange={e => updateItemField(idx, "valorUnit", e.target.value)} />
                  </span>
                </Tooltip>
                <TextField size="small" type="number" inputProps={{ min:0, step:"0.01", style:{ textAlign:"right" } }}
                  value={it.valorTotal}
                  onChange={e => updateItemField(idx, "valorTotal", e.target.value)} />
                <IconButton size="small" onClick={() => removeItem(idx)} disabled={form.items.length === 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            ))}

            <div style={{ textAlign:"right", marginTop:6 }}>
              {form.freteAtivo && freteNum > 0 && (
                <Typography variant="body2" style={{ color:"#555", marginBottom:2 }}>
                  Subtotal itens: {fmtMoney(itemsSubtotal)}
                </Typography>
              )}
              {form.freteAtivo && freteNum > 0 && (
                <Typography variant="body2" style={{ color:"#555", marginBottom:2 }}>
                  Frete: {fmtMoney(freteNum)}
                </Typography>
              )}
              <Typography variant="subtitle1" style={{ fontWeight:700 }}>
                Total: {fmtMoney(computedTotal)}
              </Typography>
            </div>
          </Grid>

          {/* ── Frete ── */}
          <Grid item xs={12}>
            <Divider style={{ marginBottom:8 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={form.freteAtivo}
                  onChange={e => setForm(f => {
                    const fa = e.target.checked;
                    const total = recalcTotal(f.items, fa, f.freteValor);
                    return { ...f, freteAtivo: fa, total };
                  })}
                  size="small"
                  style={{ color: form.freteAtivo ? "#F37021" : undefined }}
                />
              }
              label={
                <span style={{ display:"flex", alignItems:"center", gap:4, fontSize:13, fontWeight:600 }}>
                  <LocalShippingIcon fontSize="small" style={{ color: form.freteAtivo ? "#F37021" : "#aaa" }} />
                  Incluir frete
                </span>
              }
            />
            {form.freteAtivo && (
              <Grid container spacing={1} style={{ marginTop:4 }}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Endereço de entrega" size="small" fullWidth
                    value={form.freteEndereco} onChange={set("freteEndereco")}
                    placeholder="Rua, número, bairro, cidade/UF" />
                  {blingAddress && (
                    <div style={{ marginTop: 3 }}>
                      <span
                        onClick={() => setForm(f => ({ ...f, freteEndereco: blingAddress.formatted, freteCep: blingAddress.cep || f.freteCep }))}
                        style={{ fontSize: 11, color: "#F37021", cursor: "pointer", textDecoration: "underline" }}
                        title="Clique para usar o endereço cadastrado no Bling"
                      >
                        Usar endereço do Bling: {blingAddress.formatted}
                      </span>
                    </div>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="CEP" size="small" fullWidth
                    value={form.freteCep} onChange={set("freteCep")}
                    placeholder="00000-000" inputProps={{ maxLength: 9 }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Valor do frete (R$)" size="small" fullWidth type="number"
                    inputProps={{ min:0, step:"0.01" }}
                    value={form.freteValor}
                    onChange={e => setForm(f => {
                      const total = recalcTotal(f.items, f.freteAtivo, e.target.value);
                      return { ...f, freteValor: e.target.value, total };
                    })} />
                </Grid>
              </Grid>
            )}
          </Grid>

          {/* ── Observações ── */}
          <Grid item xs={12}>
            <TextField label="Observações" size="small" fullWidth multiline rows={3}
              value={form.observations} onChange={set("observations")} />
          </Grid>

        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          style={{ backgroundColor:"#F37021", color:"#fff" }}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <RequestQuoteIcon />}>
          {isEdit ? "Salvar alterações" : "Criar orçamento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuoteDialog;
